// src/contexts/SupervisorContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Supervisor, SupervisorNote, ClientFile } from "@visitproof/types";
import { SupervisorService } from "../services/SupervisorService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

// Re-type for backward compatibility
type File = ClientFile;

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface SupervisorContextProps {
  files: File[];
  notes: SupervisorNote[];
  supervisors: Supervisor[];
  selectedSupervisor: Supervisor | null;
  loading: Loadings;
  error: Errors;
  getSupervisorById: (id: string) => Supervisor | undefined;
  createSupervisor: (
    supervisorData: Omit<
      Supervisor,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ) => Promise<string>;
  updateSupervisor: (
    supervisorId: string,
    updatedData: Partial<Omit<Supervisor, "id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  setSelectedSupervisor: (supervisorId: string) => void;
  addFileToSupervisor: (
    supervisorId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ) => Promise<string>;
  deleteFileFromSupervisor: (
    supervisorId: string,
    fileId: string,
  ) => Promise<void>;
  // Notes management
  addNoteToSupervisor: (
    supervisorId: string,
    content: string,
  ) => Promise<string>;
  updateSupervisorNote: (
    supervisorId: string,
    noteId: string,
    content: string,
  ) => Promise<void>;
  deleteNoteFromSupervisor: (
    supervisorId: string,
    noteId: string,
  ) => Promise<void>;
  refreshNotes: (supervisorId: string) => Promise<void>;
}

const SupervisorContext = createContext<SupervisorContextProps | undefined>(
  undefined,
);

export const useSupervisor = (): SupervisorContextProps => {
  const context = useContext(SupervisorContext);
  if (!context) {
    throw new Error("useSupervisor must be used within a SupervisorProvider");
  }
  return context;
};

export const SupervisorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, claims } = useAuth();
  const { account } = useAccount();
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState<SupervisorNote[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] =
    useState<Supervisor | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: true,
  });
  const [error, setError] = useState<Errors>({});

  // Use accountId from claims (works for all user types) or fall back to account.id
  const accountId = claims?.accountId || account?.id;

  useEffect(() => {
    if (!accountId) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }
    setError((prev) => ({ ...prev, list: null }));
    const docRef = query(
      collection(db, "supervisors"),
      where("accountId", "==", accountId),
      orderBy("lastName", "asc"),
      orderBy("firstName", "asc"),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const supervisorsList: Supervisor[] = [];
        docSnapshot.forEach((doc) => {
          const data = doc.data();
          const supervisorData = {
            id: doc.id,
            ...data,
          } as Supervisor;
          supervisorsList.push(supervisorData);
        });
        setSupervisors(supervisorsList);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching supervisors: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );
    return () => {
      unsubscribe();
    };
  }, [accountId]);

  useEffect(() => {
    if (selectedSupervisor) {
      setSelectedSupervisor(selectedSupervisor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupervisor]);

  useEffect(() => {
    if (!selectedSupervisor) return;
    setError((prev) => ({ ...prev, details: null }));
    const docRef = doc(collection(db, "supervisors"), selectedSupervisor.id);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setSelectedSupervisor((prev) => ({ ...prev, ...data }) as Supervisor);
        }
        setLoading((prev) => ({ ...prev, details: false }));
      },
      (error) => {
        console.error("Error fetching document: ", error);
        setError((prev) => ({ ...prev, details: error }));
        setLoading((prev) => ({ ...prev, details: true }));
      },
    );
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupervisor?.id]);

  const createSupervisor = async (
    supervisorData: Omit<
      Supervisor,
      "id" | "createdAt" | "updatedAt" | "accountId" | "createdBy"
    >,
  ): Promise<string> => {
    if (!accountId || !user?.uid) {
      throw new Error("Account or user not available");
    }

    // Call the Firebase callable function to create the supervisor
    // This properly handles user lookup/creation, claims, and membership
    const createSupervisorFn = httpsCallable<
      {
        firstName: string;
        lastName: string;
        email: string;
        phoneNumber: string;
        avatarUrl?: string;
        status?: "active" | "inactive" | "pending" | "suspended";
        dateOfEmployment?: string;
      },
      {
        success: boolean;
        supervisorId: string;
        userId: string;
        isNewUser: boolean;
      }
    >(functions, "createSupervisor");

    const result = await createSupervisorFn({
      firstName: supervisorData.firstName,
      lastName: supervisorData.lastName,
      email: supervisorData.email,
      phoneNumber: supervisorData.phoneNumber,
      avatarUrl: supervisorData.avatarUrl,
      status: supervisorData.status,
      dateOfEmployment: supervisorData.dateOfEmployment
        ? new Date(supervisorData.dateOfEmployment as any).toISOString()
        : undefined,
    });

    return result.data.supervisorId;
  };

  const updateSupervisor = async (
    supervisorId: string,
    updatedData: Partial<Omit<Supervisor, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await SupervisorService.updateSupervisor(supervisorId, updatedData);
    const supervisorData = await SupervisorService.getSupervisor(supervisorId);
    setSelectedSupervisor(supervisorData);
  };

  const setSelectedSupervisorById = async (supervisorId: string) => {
    const supervisorData = await SupervisorService.getSupervisor(supervisorId);
    const fileList = await SupervisorService.getSupervisorFiles(supervisorId);
    const notesList = await SupervisorService.getSupervisorNotes(supervisorId);
    setSelectedSupervisor(supervisorData);
    setFiles(fileList);
    setNotes(notesList);
  };

  const refreshNotes = async (supervisorId: string) => {
    const notesList = await SupervisorService.getSupervisorNotes(supervisorId);
    setNotes(notesList);
  };

  const addFileToSupervisor = async (
    supervisorId: string,
    fileData: Omit<File, "id" | "createdAt" | "updatedAt" | "createdBy">,
  ): Promise<string> => {
    const newFileId = await SupervisorService.addFileToSupervisor(
      supervisorId,
      {
        ...fileData,
        createdBy: user!.uid,
      },
    );
    await setSelectedSupervisorById(supervisorId);
    return newFileId;
  };

  const deleteFileFromSupervisor = async (
    supervisorId: string,
    fileId: string,
  ): Promise<void> => {
    await SupervisorService.deleteFileFromSupervisor(supervisorId, fileId);
    await setSelectedSupervisorById(supervisorId);
  };

  const addNoteToSupervisor = async (
    supervisorId: string,
    content: string,
  ): Promise<string> => {
    if (!accountId || !user?.uid) {
      throw new Error("Account or user not available");
    }
    const noteId = await SupervisorService.addNoteToSupervisor(supervisorId, {
      content,
      createdByUserId: user.uid,
      createdByName: user.displayName || user.email || "Unknown",
      accountId,
    });
    await refreshNotes(supervisorId);
    return noteId;
  };

  const updateSupervisorNote = async (
    supervisorId: string,
    noteId: string,
    content: string,
  ): Promise<void> => {
    await SupervisorService.updateSupervisorNote(supervisorId, noteId, content);
    await refreshNotes(supervisorId);
  };

  const deleteNoteFromSupervisor = async (
    supervisorId: string,
    noteId: string,
  ): Promise<void> => {
    await SupervisorService.deleteNoteFromSupervisor(supervisorId, noteId);
    await refreshNotes(supervisorId);
  };

  const getSupervisorById = (id: string): Supervisor | undefined => {
    return supervisors.find((s) => s.id === id);
  };

  return (
    <SupervisorContext.Provider
      value={{
        loading,
        error,
        supervisors,
        selectedSupervisor,
        files,
        notes,
        getSupervisorById,
        setSelectedSupervisor: setSelectedSupervisorById,
        addFileToSupervisor,
        deleteFileFromSupervisor,
        createSupervisor,
        updateSupervisor,
        addNoteToSupervisor,
        updateSupervisorNote,
        deleteNoteFromSupervisor,
        refreshNotes,
      }}
    >
      {children}
    </SupervisorContext.Provider>
  );
};
